import React, { lazy, Suspense } from 'react';
import { Navigate, Route } from 'react-router-dom';
import Spinner from 'components-stateless/Spinner';
import { asyncRouteLoader } from 'utils/asyncRouteLoader';
import { useFlag } from 'utils/flags/hooks';
import { FEATURE_WORKFLOW_VIBEFLOWING } from 'utils/flags/constants';
import RouteAnalytics from './RouteAnalytics';

const Solutions = lazy(() => import('containers/Solutions/Solutions'));
const SolutionsChat = lazy(() => import('containers/Solutions/Chat'));
const Solution = asyncRouteLoader(
  () => import('containers/Solution'),
  [
    {
      type: 'reducer',
      reducerKey: 'solution',
      importPromise: () => import('containers/Solution/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/Version/redux/sagas'),
    },
  ]
);
const SolutionHome = lazy(() => import('containers/Solution/Solution'));
const SolutionDashboard = asyncRouteLoader(
  () => import('containers/Solution/Dashboard/SolutionDashboard'),
  [
    {
      type: 'reducer',
      reducerKey: 'solutionDashboard',
      importPromise: () => import('containers/Solution/Dashboard/reducers'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/Dashboard/sagas'),
    },
  ]
);
const SolutionVersion = lazy(() =>
  import('containers/Solution/Version').then((module) => ({
    default: module.SolutionVersion,
  }))
);
const SolutionSettings = asyncRouteLoader(
  () => import('containers/Solution/Settings/SolutionSettings'),
  [
    {
      type: 'reducer',
      reducerKey: 'solutionSettings',
      importPromise: () => import('containers/Solution/Settings/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/Settings/sagas'),
    },
  ]
);
const SolutionPermission = lazy(
  () => import('containers/Solution/Permission/SolutionPermission')
);
const DeleteSolution = asyncRouteLoader(
  () => import('containers/Solution/Delete/DeleteSolution'),
  [
    {
      type: 'reducer',
      reducerKey: 'deleteSolution',
      importPromise: () => import('containers/Solution/Delete/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/Delete/sagas'),
    },
  ]
);
const SolutionApp = asyncRouteLoader(
  () => import('containers/Solution/App'),
  [
    {
      type: 'reducer',
      reducerKey: 'solutionApp',
      importPromise: () => import('containers/Solution/App/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/App/sagas'),
    },
  ]
);
const SolutionAppTasksList = lazy(
  () => import('containers/Solution/App/Tasks/TasksList')
);
const SolutionAppTasksEditor = asyncRouteLoader(
  () => import('containers/Solution/App/Tasks/Editor'),
  [
    {
      type: 'reducer',
      reducerKey: 'appTasksEditor',
      importPromise: () =>
        import('containers/Solution/App/Tasks/Editor/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'integrations',
      importPromise: () => import('containers/Integrations/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/App/Tasks/Editor/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Integrations/sagas'),
    },
  ]
);
const SolutionWidgets = asyncRouteLoader(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  () => import('containers/Solution/App/SolutionWidgets'),
  [
    {
      type: 'reducer',
      reducerKey: 'solutionWidgets',
      importPromise: () =>
        import('containers/Solution/App/SolutionWidgets/reducer'),
    },
    {
      type: 'saga',
      importPromise: () =>
        import('containers/Solution/App/SolutionWidgets/sagas'),
    },
  ]
);
const SolutionAppInbox = asyncRouteLoader(
  () => import('containers/Solution/App/Inbox'),
  [
    {
      type: 'reducer',
      reducerKey: 'solutionInbox',
      importPromise: () => import('containers/Solution/App/Inbox/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/App/Inbox/sagas'),
    },
  ]
);
const SolutionAppSettings = asyncRouteLoader(
  () => import('containers/Solution/App/Settings'),
  [
    {
      type: 'reducer',
      reducerKey: 'appSettings',
      importPromise: () => import('containers/Solution/App/Settings/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/App/Settings/sagas'),
    },
  ]
);
const SolutionAppTasks = asyncRouteLoader(
  () => import('containers/Solution/App/Tasks'),
  [
    {
      type: 'reducer',
      reducerKey: 'appTasks',
      importPromise: () => import('containers/Solution/App/Tasks/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/App/Tasks/sagas'),
    },
  ]
);
const StudioWS = asyncRouteLoader(
  () => import('containers/StudioWS'),
  [
    {
      type: 'reducer',
      reducerKey: 'studioWS',
      importPromise: () => import('containers/StudioWS/reducer'),
    },
  ]
);
const StudioEditor = asyncRouteLoader(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  () => import('containers/StudioTabs'),
  [
    {
      type: 'reducer',
      reducerKey: 'tabs',
      importPromise: () => import('containers/StudioTabs/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'solution',
      importPromise: () => import('containers/Solution/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'formBuilder',
      importPromise: () => import('containers/FormBuilder/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'tableBuilder',
      importPromise: () => import('containers/TableBuilder/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'integrations',
      importPromise: () => import('containers/Integrations/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'studio',
      importPromise: () => import('containers/Studio/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/StudioTabs/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Studio/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/FormBuilder/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/TableBuilder/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Integrations/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solution/sagas'),
    },
  ]
);
const StudioPreview = asyncRouteLoader(
  () => import('containers/Preview'),
  [
    {
      type: 'reducer',
      reducerKey: 'preview',
      importPromise: () => import('containers/Preview/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Preview/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'team',
      importPromise: () => import('containers/Team/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'projectView',
      importPromise: () => import('containers/ProjectView/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/ProjectView/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'transcript',
      importPromise: () => import('containers/Transcript/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Transcript/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'docusignPage',
      importPromise: () => import('containers/DocusignPage/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/DocusignPage/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'preview',
      importPromise: () => import('containers/Preview/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Preview/sagas'),
    },
  ]
);

const SolutionsChatGuard = () => {
  const isVibeflowingEnabled = useFlag(FEATURE_WORKFLOW_VIBEFLOWING);
  if (!isVibeflowingEnabled) return <Navigate to="/app/solutions" replace />;
  return (
    <RouteAnalytics pageName="Solutions-Chat">
      <Suspense fallback={<Spinner />}>
        <SolutionsChat />
      </Suspense>
    </RouteAnalytics>
  );
};

export const solutionRoutes = () => (
  <>
    <Route
      path="solutions"
      element={
        <RouteAnalytics pageName="Solutions">
          <Solutions />
        </RouteAnalytics>
      }
    />
    <Route path="solutions/chat" element={<SolutionsChatGuard />} />
    <Route path="solution/:solutionId" element={<Solution />}>
      <Route
        path="versions"
        element={
          <RouteAnalytics pageName="Solution-Versions">
            <SolutionVersion />
          </RouteAnalytics>
        }
      />
      <Route
        path="settings"
        element={
          <RouteAnalytics pageName="Solution-Settings">
            <SolutionSettings />
          </RouteAnalytics>
        }
      />
      <Route
        path="permission"
        element={
          <RouteAnalytics pageName="Solution-Permission">
            <SolutionPermission />
          </RouteAnalytics>
        }
      />
      <Route
        path="delete"
        element={
          <RouteAnalytics pageName="Solution-Delete">
            <DeleteSolution />
          </RouteAnalytics>
        }
      />
      <Route path="version/:solutionVersionId" element={<SolutionApp />}>
        <Route index element={<Navigate to="home" replace />} />
        <Route
          path="home"
          element={
            <RouteAnalytics pageName="Solution-Home">
              <SolutionHome />
            </RouteAnalytics>
          }
        />
        <Route
          path="dashboard"
          element={
            <RouteAnalytics pageName="Solution-Dashboard">
              <SolutionDashboard />
            </RouteAnalytics>
          }
        />
        <Route path="app/:appId">
          <Route
            path="actions"
            element={
              <RouteAnalytics pageName="Solution-App-Tasks">
                <SolutionAppTasks />
              </RouteAnalytics>
            }
          >
            <Route index element={<Navigate to="all" replace />} />
            <Route
              path="all"
              element={
                <RouteAnalytics pageName="Solution-App-Home">
                  <SolutionAppTasksList />
                </RouteAnalytics>
              }
            />
            <Route
              path=":actionId"
              element={
                <RouteAnalytics pageName="Solution-App-Task-Editor">
                  <SolutionAppTasksEditor />
                </RouteAnalytics>
              }
            />
          </Route>
          <Route
            path="widgets"
            element={
              <RouteAnalytics pageName="Solution-App-Widgets">
                <SolutionWidgets />
              </RouteAnalytics>
            }
          />
          <Route
            path="inbox"
            element={
              <RouteAnalytics pageName="Solution-App-Inbox">
                <SolutionAppInbox />
              </RouteAnalytics>
            }
          />
          <Route
            path="settings"
            element={
              <RouteAnalytics pageName="Solution-App-Settings">
                <SolutionAppSettings />
              </RouteAnalytics>
            }
          />
          <Route
            path="studio/:module_version_uuid"
            element={
              <Suspense fallback={<Spinner />}>
                <StudioWS />
              </Suspense>
            }
          >
            <Route index element={<Navigate to="editor" replace />} />
            <Route
              path="editor"
              element={
                <RouteAnalytics pageName="Solution-App-Studio">
                  <StudioEditor />
                </RouteAnalytics>
              }
            />
            <Route
              path="preview"
              element={
                <RouteAnalytics pageName="Solution-App-Studio-Preview">
                  <StudioPreview />
                </RouteAnalytics>
              }
            />
          </Route>
        </Route>
      </Route>
    </Route>
  </>
);
